/**
 * Découpe un texte en segments de prose et de mathématiques.
 *
 * C'est la brique partagée par les deux plateformes et par les deux surfaces
 * de dialogue (session Sam, checkpoints) : le web la donne à KaTeX, le natif
 * à son propre compositeur, et l'apparition progressive des checkpoints s'en
 * sert pour dévoiler la prose au caractère tout en posant les formules d'un
 * bloc — on ne dévoile pas `\frac` lettre à lettre.
 *
 * Délimiteurs reconnus : `$…$` et `\(…\)` en ligne, `$$…$$` et `\[…\]` en
 * bloc. Un `$` non refermé reste du texte : un élève qui écrit « ça coûte
 * 5 $ » ne doit pas voir sa phrase avalée par un mode mathématique.
 */

import { normalizeEnonceLatex } from "@common/utils/enonceLatex"
import { repairBareMacros, restoreMutilatedMacros } from "./latexToUnicode"

// Séquences ANSI (ESC [ … lettre) et caractères de contrôle. Un modèle en
// produit parfois — constaté en recette : des NUL et des séquences ANSI
// collés aux délimiteurs, qui s'affichaient en carrés à l'écran ET
// empêchaient la formule d'être reconnue. Le service IA les retire désormais
// à la source, mais l'affichage ne fait pas confiance à ce qu'il reçoit : les
// conversations déjà en cache en contiennent, et un autre producteur pourrait
// en réintroduire.
// eslint-disable-next-line no-control-regex
const ANSI = /\x1b\[[0-9;]*[A-Za-z]/g
// Tabulation comprise : après la reconstitution des commandes, une tabulation
// restante n'est plus qu'un blanc parasite dans une bulle.
// eslint-disable-next-line no-control-regex
const CONTROL = /[\x00-\x09\x0b-\x1f\x7f-\x9f]/g
// Un délimiteur suivi d'un blanc insécable n'ouvre pas un mode mathématique
// pour les analyseurs Markdown+maths, qui exigent un caractère non blanc.
const HARD_SPACES = new RegExp("[\u00a0\u202f]", "g")

// Commande sur-échappée : le modèle applique l'échappement JSON au contenu et
// envoie `\\frac`. Dans une bulle de deux phrases, `\\` n'est jamais le retour
// à la ligne LaTeX.
const OVER_ESCAPED = /\\{2,}(?=[a-zA-Z])/g
// Reste du même excès : un `\n` sur-échappé n'est plus un saut de ligne mais
// deux caractères, que l'élève lit tels quels. Sauf devant une minuscule, où
// `\nabla`, `\ne` et `\notin` sont en jeu.
const LITERAL_NEWLINE = /\\n(?![a-z])/g

/**
 * Nettoie un texte produit par un modèle, avant tout affichage.
 *
 * @param {string} input
 * @returns {string}
 */
export function sanitizeRichText(input) {
    if (!input || typeof input !== "string") return ""
    // Reconstituer les commandes AVANT de retirer les contrôles : c'est la
    // tabulation elle-même qui porte la lettre disparue.
    return restoreMutilatedMacros(input)
        .replace(ANSI, "")
        .replace(CONTROL, "")
        .replace(OVER_ESCAPED, "\\")
        .replace(LITERAL_NEWLINE, "\n")
        .replace(HARD_SPACES, " ")
}

// Sentinelle pour `\$` — un vrai dollar dans le texte, jamais un délimiteur.
// Zone à usage privé : aucun texte d'élève ni de modèle ne la contient, et
// elle reste VISIBLE dans le code. La version précédente utilisait des octets
// NUL, invisibles à la lecture et justement filtrés par `sanitizeRichText`.
const PUA = String.fromCharCode(0xe000)
const ESCAPED_DOLLAR = PUA + "ESCAPED_DOLLAR" + PUA

// `\(…\)` et `\[…\]` ne figurent pas ici : `normalizeEnonceLatex` les a déjà
// réécrits en `$…$` / `$$…$$`, avant même que ce module ne voie la source.
const DELIMITERS = [
    { open: "$$", close: "$$", display: true },
    { open: "$", close: "$", display: false },
]

/**
 * Un contenu de délimiteur qui est en fait une phrase.
 *
 * Deuxième garde contre le même dégât : même bien apparié, un `$…$` peut
 * enfermer de la prose si le modèle a mal placé ses marqueurs — il lui suffit
 * de parler du signe dollar lui-même. La phrase part alors en mode
 * mathématique, où les espaces sont supprimés : illisible.
 *
 * Deux marqueurs de prose, et pas la présence d'un chiffre — « on l'écrit
 * juste après le nombre : 5 » en contient un, et c'est bien une phrase :
 *
 * - une apostrophe, qu'une formule ne porte jamais ;
 * - trois mots de trois lettres ou plus. Les opérandes d'une formule de
 *   collège sont des lettres isolées ou des nombres ; les mots y arrivent
 *   par `\text{…}` ou `\frac{…}{…}`, donc avec un antislash.
 */
const looksLikeProse = (content) => {
    // L'antislash tranche en premier : une commande — donc une formule — peut
    // très bien porter une apostrophe française, par exemple dans l'argument
    // d'un \text{…} (« \text{l'aire} »). Tester l'apostrophe avant l'antislash
    // faisait échouer CE cas précis, celui que la garde vise à protéger.
    if (content.includes("\\")) return false
    if (/['’]/.test(content)) return true
    return (content.match(/[A-Za-zÀ-ÿ]{3,}/g) || []).length >= 3
}

const pushText = (segments, value) => {
    if (!value) return
    const text = value.split(ESCAPED_DOLLAR).join("$")
    const last = segments[segments.length - 1]
    // Deux morceaux de prose consécutifs (une formule non refermée, par
    // exemple) se recollent : les consommateurs n'ont pas à gérer ce cas.
    if (last && last.type === "text") last.value += text
    else segments.push({ type: "text", value: text })
}

/**
 * @param {string} input
 * @returns {Array<{type:"text"|"math", value:string, display?:boolean}>}
 */
export function splitLatexSegments(input) {
    if (!input || typeof input !== "string") return []

    // Nettoyer AVANT de poser la sentinelle : l'entrée peut contenir les
    // caractères de contrôle qu'on filtre. Même normalisation que
    // l'affichage des énoncés : une bulle et un énoncé doivent reconnaître
    // les mêmes délimiteurs.
    const source = normalizeEnonceLatex(sanitizeRichText(input))
        .split("\\$")
        .join(ESCAPED_DOLLAR)
    const segments = []
    let buffer = ""
    let i = 0

    while (i < source.length) {
        const delimiter = DELIMITERS.find((d) => source.startsWith(d.open, i))

        if (!delimiter) {
            buffer += source[i]
            i += 1
            continue
        }

        const contentStart = i + delimiter.open.length
        const closeAt = source.indexOf(delimiter.close, contentStart)

        // Pas de fermeture : ce n'était pas un délimiteur. Le marqueur part
        // en texte EN ENTIER. N'avancer que d'un caractère laissait le second
        // `$` d'un `$$` non refermé disponible pour s'apparier avec un `$`
        // plus loin — la prose intermédiaire passait en mode mathématique, et
        // KaTeX la composait en italique sans espaces, illisible.
        if (closeAt === -1) {
            buffer += delimiter.open
            i += delimiter.open.length
            continue
        }

        const content = source.slice(contentStart, closeAt)
        // `$$` collés, ou `$ $` : rien à composer, on garde le littéral.
        if (!content.trim() || looksLikeProse(content)) {
            buffer += source.slice(i, closeAt + delimiter.close.length)
            i = closeAt + delimiter.close.length
            continue
        }

        pushText(segments, buffer)
        buffer = ""
        segments.push({
            type: "math",
            // `repairBareMacros` ici plutôt que chez chaque consommateur :
            // web, natif et dévoilement progressif doivent voir la même
            // formule réparée.
            value: repairBareMacros(
                content.split(ESCAPED_DOLLAR).join("$").trim()
            ),
            display: delimiter.display,
        })
        i = closeAt + delimiter.close.length
    }

    pushText(segments, buffer)
    return segments
}

/** Vrai dès qu'au moins un segment est mathématique. */
export function hasMath(input) {
    return splitLatexSegments(input).some((s) => s.type === "math")
}
